import { fetchDocument } from './lumina-request.js';
import { loadQuickViewResources } from './lumina-quick-view-loader.js';
const { Core, Utils, DOMEvents, Events, TopLayerStack } = Global;
const { $fetch, $replaceContent, $active } = Utils;

const ENTER_KEY_IDENTIFIER = 'Enter';

const animationsComplete = element =>
  Promise.allSettled(
    element.getAnimations().map(animation => 
      animation.finished))

customElements.define('modal-container', class extends Core {
    elements = {
        $: ['dialog', 'content', 'spinner']
    }

    propTypes = {
        'remote-url': String,
        'remote-target': String,
        'remote-detach': Boolean,
        // 'top-layer': Boolean,
    }

    render() {
        this.$('dialog', {
            cancel: event => { event.preventDefault(); this.close(); },
            click: this._handleBackdropClick
        })

        this.$({
            'modal-close': this._close,
            'modal-open': this._open
        })
    }

    open() {
        // this.dispatchEvent(DOMEvents[this.prop('top-layer') ? 'MODAL_OPEN' : 'MODAL_SHOW']);
        this.dispatchEvent(DOMEvents.MODAL_OPEN);
    }

    close() {
        this.dispatchEvent(DOMEvents.MODAL_CLOSE);
    }

    refreshLayer() {
        this.$('dialog').close();
        this.$('dialog').showModal();
    }

    _handleBackdropClick({ target }) {
        if(target === this.$('dialog')) {
            this.dispatchEvent(DOMEvents.MODAL_CLOSE);
        }
    }
    
    async _loadRemoteContent() {
        this._loading(true)();
        try {
            const doc = await fetchDocument(this.prop('remote-url'));
            const content = doc.querySelector(this.prop('remote-target'));
            if (!content) throw new Error('Modal content is missing');
            if (this.prop('remote-target') === '#ProductQuickView') await loadQuickViewResources(content);
            this.$remoteContent = content;
        } finally {
            this._loading(false)();
        }
    }

    _showRemoteError() {
        const box = document.createElement('div');
        box.setAttribute('role', 'alert');
        box.className = 'lumina-modal-error';
        const text = document.createElement('p');
        text.textContent = window.luminaStrings?.modalError || '';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = window.luminaStrings?.retry || '';
        retry.addEventListener('click', async () => {
            retry.disabled = true;
            try { await this._handleRemoteContent(); }
            catch (error) { console.error(error); retry.disabled = false; }
        });
        box.append(text, retry);
        this.$('content').replaceChildren(box);
    }

    _loading(state) {
        return () => {
            $active(this.$('spinner'), state);
        }
    }

    _attachRemoteContent() {
        if(this.$remoteContent && this.$remoteContent.parentElement !== this.$('content')) {
            this.$('content').replaceChildren(this.$remoteContent);
        }
    }

    _detachRemoteContent() {
        if(this.$remoteContent && this.prop('remote-detach')) {
            this.$remoteContent.remove();
        }
    }

    async _handleRemoteContent() {
        if(this.prop('remote-url') && this.prop('remote-target') && !this.$remoteContent) {
            await this._loadRemoteContent();
        }

        this._attachRemoteContent();
    }
    
    _onClose(cb) {
        this.addEventListener('modal-close', cb, {once: true});
    }

    async _open(e) {
        if(this.$('dialog').open) {
            return;
        }
        TopLayerStack.add(this.$('dialog'), this._onClose.bind(this));        
        // this.$('dialog')[e.type === 'modal-open' ? 'showModal': 'show']();
        this.returnFocus = document.activeElement;
        this.$('dialog').showModal();

        try { await this._handleRemoteContent(); }
        catch (error) { console.error(error); this._showRemoteError(); }
        if (!this.$('dialog').open) return;
        this.setAttribute('opening', '');

        await animationsComplete(this.$('content')); 
        this.removeAttribute('opening');

        this.$('dialog').dispatchEvent(new CustomEvent('modal-open', {
            bubbles: true
        }))
    }

    async _close(e) {
        e?.preventDefault();
        if(!this.$('dialog').open) {
            return;
        }
        TopLayerStack.remove();
        this.setAttribute('closing', '');
        await animationsComplete(this.$('content'));
        this.$('dialog').close();
        this.removeAttribute('closing');

        this._detachRemoteContent();
        if (this.returnFocus?.isConnected) this.returnFocus.focus();
    }
})

customElements.define('modal-open', class extends Core {
    propTypes = {
        target: String,
        fallback: String
    }

    render() {
        this.$target = document
            .getElementById(this.prop('target'))

        this._handleClick();
    }

    async _handleClick() {
        if (!this.querySelector('button, a, input') && !this.hasAttribute('tabindex')) {
            this.tabIndex = 0;
            this.setAttribute('role', 'button');
        }
        await customElements.whenDefined('modal-container');

        this.$({
            click: this._openTargetModal,
            keydown: this._openOnEnterPressed
        })
    }

    _openOnEnterPressed(e) {
        if((e.key === ENTER_KEY_IDENTIFIER || e.key === ' ') && e.target === this) {
            e.preventDefault();
            e.stopPropagation();
            this._openTargetModal()
        }
    }

    _openTargetModal() {
        if(!this.$target) {
            if(this.prop('fallback')) {
                window.location.href = this.prop('fallback');
            }
            return;
        }
        this.$target.dispatchEvent(DOMEvents.MODAL_OPEN);
    }
})

customElements.define('modal-close', class extends Core {
    render() {
        if (!this.querySelector('button, a')) { this.tabIndex = 0; this.setAttribute('role', 'button'); }
        this.$({
            keydown: event => {
                if ((event.key === 'Enter' || event.key === ' ') && event.target === this) {
                    event.preventDefault(); this._emitClose();
                }
            },
            click: this._emitClose
        })
    }

    _emitClose() {
        this.dispatchEvent(DOMEvents.MODAL_CLOSE);
    }
})
